import { DateTime } from 'luxon';
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UserForm from "../components/UserForm";
import AuthPage from "../pages/AuthPage";
import "../styles/main.css";
import { computeTrueSolarTime } from "../utils/computeTrueSolarTime";
import { computeBaziPillars } from "../utils/computeBaziPillars";
import { useLanguage } from "../contexts/LanguageContext";
import { supabase } from "../utils/supabaseClient";

export default function HomePage({ session }) {
  const [isLoading, setIsLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const { language, setLanguage, setLanguageLocal } = useLanguage();
  // State for Logout Confirmation Modal
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (session) {
      navigate('/dashboard');
    }
  }, [session, navigate]);

  useEffect(() => {
    sessionStorage.setItem("desti_lang", language);
  }, [language]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const closeDropdown = (e) => {
      if (showDropdown && !e.target.closest('.auth-top')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', closeDropdown);
    return () => document.removeEventListener('click', closeDropdown);
  }, [showDropdown]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const storedData = sessionStorage.getItem('baziResults');
      if (storedData) {
        try {
          const data = JSON.parse(storedData);
          if (data.formData) {
            delete data.formData;
            sessionStorage.setItem('baziResults', JSON.stringify(data));
          }
        } catch (error) {
          sessionStorage.removeItem('baziResults');
        }
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    async function fetchProfile() {
      if (!session?.user?.id) return;

      const { data, error } = await supabase
        .from('USER_PROFILES')
        .select('*')
        .eq('user_id', session.user.id)
        .single();

      if (error) {
        console.error("Error fetching profile:", error);
      } else if (data && data.date_of_birth) {
        const dt = DateTime.fromISO(data.date_of_birth);
        setInitialData({
          birthDate: dt.toISODate(),
          birthHour: dt.hour,
          birthMinute: dt.minute,
          city: data.city_of_birth || "",
          gender: data.gender_at_birth || "",
          mbti: data.mbti || "",
          country: data.country_of_birth || ""
        });
      }
    }
    fetchProfile();
  }, [session]);

  useEffect(() => {
    const syncPendingData = async () => {
      if (session?.user?.id) {
        const stored = sessionStorage.getItem('baziResults');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (parsed.formData) {
              console.log("Found pending data, syncing...");
              // await saveDataToProfile(parsed.formData, session.user.id); // Disabled auto-save to profile
              setInitialData(parsed.formData);
            }
          } catch (e) {}
        }
      }
    };
    syncPendingData();
  }, [session]);

  const handleSubmit = async (formData) => {
    setIsLoading(true);
    try {
      const { birthDate, birthHour, birthMinute, longitude, timeZone } = formData;
      const lang = formData?.language || language;

      let trueSolarTime;
      if (longitude != null) {
        trueSolarTime = computeTrueSolarTime(birthDate, birthHour, birthMinute, longitude, timeZone);
      } else {
        trueSolarTime = DateTime.fromISO(`${birthDate}T${String(birthHour).padStart(2, '0')}:${String(birthMinute).padStart(2, '0')}`);
      }
      const tstISO = trueSolarTime ? trueSolarTime.toISO() : null;
      const baziPillars = computeBaziPillars(trueSolarTime, lang);

      // Navigate immediately to results page
      // The ResultsPage will handle the API call and profile saving
      const resultsData = {
        trueSolarTime: tstISO,
        baziPillars: baziPillars,
        prediction: "",
        language: lang,
        isLoading: true, // Flag to indicate analysis is pending
        formData: { ...formData, language: lang }
      };

      sessionStorage.setItem('baziResults', JSON.stringify(resultsData));
      sessionStorage.setItem('desti_lang', lang);
      navigate('/results');

    } catch (err) {
      console.error("Error during computation:", err);
      const resultsData = {
        trueSolarTime: null,
        baziPillars: null,
        prediction: "Failed to compute analysis.",
        language: formData?.language || language,
        isLoading: false,
        formData: { ...formData, language: formData?.language || language }
      };
      sessionStorage.setItem('baziResults', JSON.stringify(resultsData));
      navigate('/results');
    } finally {
      setIsLoading(false);
    }
  };

  const openAuth = () => {
    setShowAuth(true);
  };

  return (
    <div className="desti-container ethereal-theme">
      <div className="auth-top">
        <button
          className="lang-toggle auth-small-btn"
          onClick={() => {
            const next = language === "en" ? "zh" : "en";
            // Use local setter to avoid persisting to Supabase
            if (typeof setLanguageLocal === 'function') {
              setLanguageLocal(next);
            } else {
              // Fallback to setLanguage but this may persist; ideally context provides local setter
              setLanguage(next);
            }
            sessionStorage.setItem("desti_lang", next);
          }}
          aria-label="Toggle language"
          style={{ marginRight: 8 }}
        >
          🌐 {language === "en" ? "中文" : "En"}
        </button>

        {!session ? (
          <button className="auth-small-btn" onClick={() => openAuth()} aria-label="Sign in or Sign up">
            {language === "en" ? "Log In" : "登录"}
          </button>
        ) : (
          <div style={{ position: 'relative' }}>
            <button 
              className="avatar-btn" 
              onClick={() => setShowDropdown(!showDropdown)}
              title={session.user.email}
            >
              {(session.user.email || "U").charAt(0).toUpperCase()}
            </button>

            {showDropdown && (
              <div className="dropdown-menu">
                <div className="dropdown-header">
                  <span className="dropdown-user-email" style={{ fontSize: '0.9rem', color: '#333', fontWeight: 'bold' }}>
                    {session.user.email}
                  </span>
                </div>
                
                <button className="dropdown-item" onClick={() => navigate('/dashboard')}>
                  {language === "en" ? "📊 Dashboard" : "📊 仪表板"}
                </button>

                <button className="dropdown-item" onClick={() => navigate('/profile')}>
                  {language === "en" ? "👤 Profile" : "👤 个人资料"}
                </button>
                
                <div style={{ height: 1, background: 'rgba(0,0,0,0.05)', margin: '4px 0' }} />
                
                <button 
                  className="dropdown-item danger" 
                  onClick={() => {
                    setShowDropdown(false);
                    setShowLogoutConfirm(true);
                  }}
                >
                  {language === "en" ? "🚪 Log Out" : "🚪 退出登录"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="page-content">
        <UserForm
          onSubmit={handleSubmit}
          isLoading={isLoading}
          language={language}
          initialValues={initialData}
        />
      </div>

      {showAuth && <AuthPage onClose={() => setShowAuth(false)} />}

      {/* --- LOGOUT CONFIRMATION MODAL --- */}
      {showLogoutConfirm && (
        <div className="auth-modal-backdrop" onClick={() => setShowLogoutConfirm(false)} style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', padding: '25px', borderRadius: '8px',
              textAlign: 'center', minWidth: '300px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '15px' }}>
              {language === "en" ? "Log Out?" : "确认登出?"}
            </h3>
            <p style={{ marginBottom: '25px', color: '#666' }}>
              {language === "en"
                ? "Are you sure you want to sign out?"
                : "您确定要退出登录吗?"}
            </p>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              {/* FIXED CANCEL BUTTON COLOR */}
              <button
                onClick={() => setShowLogoutConfirm(false)}
                style={{
                  padding: '8px 20px', border: '1px solid #ccc',
                  backgroundColor: 'white',
                  color: '#333', // <--- Force dark text color
                  borderRadius: '4px', cursor: 'pointer'
                }}
              >
                {language === "en" ? "Cancel" : "取消"}
              </button>

              <button
                onClick={async () => {
                  await supabase.auth.signOut();
                  setShowLogoutConfirm(false);
                  navigate("/");
                }}
                style={{
                  padding: '8px 20px', border: 'none',
                  backgroundColor: '#dc3545', color: 'white',
                  borderRadius: '4px', cursor: 'pointer'
                }}
              >
                {language === "en" ? "Log Out" : "确认登出"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}